import { EmployerHeader, EmployerFooter } from '@seek/adv-header-footer';
import { useHirerAuth } from '@seek/hirer-auth-react';
import { useAccountContext } from '@seek/hirer-graphql-react';
import { useHirerSiftLoader } from '@seek/hirer-sift-wrapper';
import type { Locale } from '@seek/melways-sites';
import { Box } from 'braid-design-system';
import {
  type ChangeEvent,
  Fragment,
  type FunctionComponent,
  type ReactNode,
  useEffect,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { SystemBanner } from 'src/app/shared/components/SystemBanner';
import { UivController } from 'src/app/shared/components/UivController';
import { TRACKING_ORIGIN } from 'src/app/shared/constants/headerFooterTracking';
import { getEnvironment, isStaging } from 'src/app/utils/environment';
import { log } from 'src/app/utils/logging';
import { getHeaderFooterInteraction } from 'src/app/v1/analytics/headerFooter';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateUserCanManageUsers } from 'src/app/v1/state/modules/session/userCanManageUsers';
import { actionUpdateUserCanPayInvoices } from 'src/app/v1/state/modules/session/userCanPayInvoices';
import {
  headerLinkRenderer,
  footerLinkRenderer,
} from 'src/app/v1/views/components/TrackingLink';
import { PreviewSection } from 'src/app/v1/views/sections/PreviewSection';
import type { EmployerSiteName } from 'src/types/app';
import type { AppState } from 'src/types/appState';
import type { TealiumEventData } from 'src/types/trackingData';

import * as styles from './index.css';

const onSiftError = (error: unknown) => {
  log.error('loadSift | Error', { error });
};

interface Props {
  children: ReactNode;
  language: Locale;
  site: EmployerSiteName;
}

export const MainLayout: FunctionComponent<Props> = ({
  children,
  language,
  site,
}) => {
  const { pageId } = useSelector(
    (state: AppState) => ({
      pageId: state.session.pageId,
    }),
    shallowEqual,
  );

  const isSuccessPage = pageId && pageId.match(/success/i);

  const dispatch = useDispatch();
  const { loading, authenticated } = useHirerAuth();

  const { accountContext } = useAccountContext({
    skip: !authenticated,
  });

  const headerFooterContext = authenticated
    ? accountContext?.headerFooterContext
    : { identityContext: null };

  const handleHeaderToggles = (event: ChangeEvent<any>) => {
    const trackingEvent = event.target.getAttribute('data-analytics');
    const headerToggleData: TealiumEventData = getHeaderFooterInteraction(
      trackingEvent,
      TRACKING_ORIGIN.HEADER,
    );
    const eventData = {
      eventName: trackingEvent,
      objectInteraction: headerToggleData.objectInteraction,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  useEffect(() => {
    if (accountContext) {
      dispatch(
        actionUpdateUserCanManageUsers(
          accountContext.headerFooterContext?.advertiser?.permissionOptions
            .canManageUsers,
        ),
      );
      dispatch(
        actionUpdateUserCanPayInvoices(
          accountContext.headerFooterContext?.advertiser?.permissionOptions
            .canPayInvoices,
        ),
      );
    }
  }, [accountContext, dispatch]);

  useHirerSiftLoader({
    environment: getEnvironment(),
    onError: onSiftError,
  });

  if (loading) {
    return null;
  }

  return (
    <Fragment>
      <Box className={styles.root}>
        <UivController />
        <EmployerHeader
          {...headerFooterContext}
          basic={!isSuccessPage}
          data-testid="employerHeader"
          site={site}
          locale={language}
          linkRenderer={headerLinkRenderer}
          onToggleButtonClick={handleHeaderToggles}
          isStaging={isStaging()}
          divider
        />
        <SystemBanner context="job_posting_global" />
        <Box background="body" component="main" className={styles.content}>
          {children}
        </Box>
        {/* @ts-ignore */}
        <EmployerFooter
          {...headerFooterContext}
          site={site}
          locale={language}
          linkRenderer={footerLinkRenderer}
          isStaging={isStaging()}
          basic
        />
      </Box>
      <PreviewSection />
    </Fragment>
  );
};
